import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import React from 'react';

import Layout from '@/layouts';
import { page3FaqJsonLd } from '@/lib/jsonld';
import { getTdkApi } from '@/lib/service/tdk';
import { AppSettings } from '@/shared/app-common';
import { canonicalUrl } from '@/shared/utils';
import { NextPageWithLayout } from '../_app';
import { fetchLoadingDataWith } from '@/lib/service/home';
import { getFaqList } from '@/lib/utils/landingData';

const ThreadsDownloader = dynamic(() => import('@/components/ThreadsDownloader'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;

  // tracker 模块 - 主功能
  const path = '/threads-downloader';

  const [localeRes, tdkRes, dataRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(path, locale),
    fetchLoadingDataWith(path, locale),
  ]);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;
  const resultFaqList = getFaqList(dataRes);

  const jsonLd = page3FaqJsonLd(
    {
      title: tdkRes?.data?.title ?? 'Threads Downloader',
      desc:
        tdkRes?.data?.description ??
        tempData[
          `Whether you're backing up your posts, sharing content elsewhere, or watching videos offline, Dolphin Radar makes it easy to download Threads videos, GIFs, or images in high quality. Just paste a link, click download, and you're done — completely free.`
        ],
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    { url: siteUrl },
    resultFaqList,
    {
      name: 'Threads Downloader – Download Threads Videos, GIFs & Images in HD',
      url: 'https://www.dolphinradar.com/threads-downloader',
      applicationCategory: 'Threads Downloader',
      description: 'Download videos, images, and GIFs from Threads quickly and easily',
      featureList: ['Threads Video Downloader', 'Threads GIF Downloader', 'Threads Image Downloader'],
    }
  );
  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkRes?.data?.title ?? 'DolphinRadar - ' + tempData['Threads Downloader'],
      description:
        tdkRes?.data?.description ??
        tempData[
          `Whether you're backing up your posts, sharing content elsewhere, or watching videos offline, Dolphin Radar makes it easy to download Threads videos, GIFs, or images in high quality. Just paste a link, click download, and you're done — completely free.`
        ],
      contentData: dataRes?.data ?? null,
      jsonLd,
    },
  };
};

const IndexPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <ThreadsDownloader data={props} />;
};

IndexPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default IndexPage;
